import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { 
  Shield, Lock, Wallet, PieChart, Smartphone, Zap, 
  EyeOff, Database, Brain, CreditCard, Target, Calculator,
  ArrowRight, Check, Github, Mail, ChevronRight,
  Building2, TrendingUp, Monitor, MessageSquare, Sparkles
} from "lucide-react";

// Lazy load analytics - not needed for initial render
const SpeedInsights = lazy(() => import("@vercel/speed-insights/react").then(m => ({ default: m.SpeedInsights })));

// Lazy import axios - only needed for email signup section
const axiosModule = () => import("axios");

// Use relative API path for Vercel deployment (frontend and backend on same domain)
// For local development, set REACT_APP_BACKEND_URL=http://localhost:8000 in .env
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = (BACKEND_URL && BACKEND_URL !== 'undefined') ? `${BACKEND_URL}/api` : '/api';

// App Screenshots - Optimized WebP served from same origin (Vercel CDN)
const screenshots = {
  mainScreen: "/screenshots/Dashboard.webp",
  expenses: "/screenshots/Expenses.webp",
  budgets: "/screenshots/Budget-Screen.webp",
  goals: "/screenshots/Goals.webp",
  askAI: "/screenshots/AIInsights.webp",
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// Feature data - Updated v4.0.0
const features = [
  { icon: Brain, title: "AI Finance Assistant", desc: "Natural language chat for financial queries" },
  { icon: Target, title: "Goal Planner", desc: "Create savings goals with timelines" },
  { icon: Calculator, title: "Safe-to-Spend", desc: "Check if purchases fit your budget" },
  { icon: Smartphone, title: "Smart SMS Parsing", desc: "Auto-capture bank transactions" },
  { icon: PieChart, title: "Budget Analytics", desc: "6-month histogram tracking" },
  { icon: Wallet, title: "Auto-Categorization", desc: "AI learns from your habits" },
  { icon: Database, title: "Encrypted Storage", desc: "SQLCipher protected data" },
  { icon: Monitor, title: "Multi-Platform", desc: "Android, iOS & Desktop support" },
];

// Privacy features
const privacyFeatures = [
  { icon: EyeOff, text: "Never reads personal chats" },
  { icon: Lock, text: "Never reads OTP messages" },
  { icon: Shield, text: "Never uploads your data" },
  { icon: Database, text: "Local-only encryption" },
];

// Timeline data - Updated for v4.0.0
const timeline = [
  { phase: "Android", status: "current", desc: "v4.0 with AI Released" },
  { phase: "iOS", status: "upcoming", desc: "Coming Q2 2026" },
  { phase: "Desktop", status: "planned", desc: "Windows, Mac, Linux" },
];

// Hero Section Component
const HeroSection = () => {
  return (
    <section className="hero-bg min-h-screen flex items-center justify-center relative px-6 py-20">
      {/* Floating blobs */}
      <div className="blob blob-1" />
      <div className="blob blob-2" />
      <div className="blob blob-3" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <motion.div 
            className="text-left"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Coming Soon Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-spend-card border border-spend-border mb-8"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Sparkles className="w-4 h-4 text-spend-accent-primary" />
              <span className="text-sm font-medium text-spend-text-secondary">v4.0 with AI Assistant</span>
            </motion.div>
            
            {/* Logo/Title */}
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 tracking-tight" data-testid="hero-title">
              <span className="gradient-text">SpendArc</span>
            </h1>
            
            {/* Tagline */}
            <p className="text-xl sm:text-2xl text-spend-text-primary font-heading font-semibold mb-4">
              AI-Powered Privacy-First Finance Tracker
            </p>
            
            {/* Sub-tagline */}
            <p className="text-lg text-spend-text-secondary mb-10 max-w-lg">
              Ask AI about your spending, set savings goals, and check if purchases are safe — all while your data stays 100% on your device.
            </p>
            
            {/* Download buttons */}
            <div className="flex flex-wrap gap-4">
              <button 
                disabled
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-spend-card border border-spend-border text-spend-text-muted cursor-not-allowed opacity-60"
                data-testid="download-android-btn"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.523 15.341a.5.5 0 00-.858-.344l-1.32 1.32-1.65-1.65a.5.5 0 00-.707 0l-1.65 1.65-1.32-1.32a.5.5 0 00-.858.344v4.632c0 .276.224.5.5.5h7.363a.5.5 0 00.5-.5v-4.632zM6.341 6.523a.5.5 0 00-.344.858l1.32 1.32-1.65 1.65a.5.5 0 000 .707l1.65 1.65-1.32 1.32a.5.5 0 00.344.858h4.632a.5.5 0 00.5-.5V7.023a.5.5 0 00-.5-.5H6.341z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">Android</div>
                  <div className="text-sm font-semibold">Launching Soon</div>
                </div>
              </button>
              
              <button 
                disabled
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-spend-card border border-spend-border text-spend-text-muted cursor-not-allowed opacity-60"
                data-testid="download-ios-btn"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">iOS</div>
                  <div className="text-sm font-semibold">Coming Later</div>
                </div>
              </button>
            </div>
            
            {/* GitHub link */}
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://github.com/atomickashyap/SpendArc-Public"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-spend-paper border border-spend-border text-spend-text-primary hover:border-spend-accent-primary hover:bg-spend-card transition-all duration-300 group"
              >
                <Github className="w-4 h-4 group-hover:text-spend-accent-primary transition-colors" />
                <span className="text-sm font-medium">View on GitHub</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
          
          {/* Right content - Phone mockup */}
          <motion.div 
            className="relative flex justify-center"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="phone-frame phone-shine w-[280px] sm:w-[320px] bg-spend-bg">
              <img 
                src={screenshots.mainScreen} 
                alt="SpendArc Dashboard" 
                className="w-full h-auto"
                width={320}
                height={693}
                fetchPriority="high"
                data-testid="hero-phone-mockup"
              />
            </div>
            
            {/* Floating AI card */}
            <motion.div 
              className="absolute -left-4 top-1/4 glass rounded-xl p-4 border border-spend-border shadow-lg"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-spend-accent-primary/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-spend-accent-primary" />
                </div>
                <div>
                  <p className="text-xs text-spend-text-muted">Ask SpendArc</p>
                  <p className="text-sm font-heading font-bold text-spend-text-primary">AI Assistant</p>
                </div>
              </div>
            </motion.div>
            
            {/* Privacy badge */}
            <motion.div 
              className="absolute -right-4 bottom-1/4 glass rounded-xl p-4 border border-spend-border shadow-lg privacy-pulse"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-spend-accent-success/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-spend-accent-success" />
                </div>
                <div>
                  <p className="text-xs text-spend-text-muted">Data Privacy</p>
                  <p className="text-sm font-heading font-semibold text-spend-accent-success">100% Local</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Features Section Component
const FeaturesSection = () => {
  return (
    <section className="py-24 px-6 relative" id="features">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-spend-text-primary" data-testid="features-title">
            Everything You Need
          </h2>
          <p className="text-spend-text-secondary max-w-2xl mx-auto">
            Powerful features designed with your privacy in mind
          </p>
        </motion.div>
        
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="feature-card bg-spend-paper border border-spend-border rounded-xl p-6 card-hover"
              variants={fadeInUp}
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 rounded-lg bg-spend-accent-primary/10 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-spend-accent-primary icon-glow" />
              </div>
              <h3 className="font-heading font-semibold text-spend-text-primary mb-2">
                {feature.title}
              </h3>
              <p className="text-sm text-spend-text-secondary">
                {feature.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Privacy Section Component
const PrivacySection = () => {
  return (
    <section className="py-24 px-6 relative overflow-hidden" id="privacy">
      {/* Background glow */}
      <div className="absolute inset-0 gradient-glow" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-spend-accent-success/10 border border-spend-accent-success/20 mb-6">
              <Shield className="w-4 h-4 text-spend-accent-success" />
              <span className="text-sm font-medium text-spend-accent-success">Privacy First</span>
            </div>
            
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6 text-spend-text-primary" data-testid="privacy-title">
              Your Data Stays Yours
            </h2>
            
            <p className="text-spend-text-secondary mb-10 text-lg">
              SpendArc is built from the ground up with privacy as the core principle. 
              All your financial data stays on your device, encrypted and secure.
            </p>
            
            <div className="space-y-4">
              {privacyFeatures.map((item, index) => (
                <motion.div 
                  key={item.text}
                  className="flex items-center gap-4 p-4 rounded-lg bg-spend-paper/50 border border-spend-border"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`privacy-feature-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-spend-accent-success/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-spend-accent-success" />
                  </div>
                  <span className="text-spend-text-primary font-medium">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          {/* Right content - Lock illustration */}
          <motion.div 
            className="flex justify-center"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              {/* Central lock */}
              <div className="w-48 h-48 rounded-full bg-spend-paper border-2 border-spend-accent-success/30 flex items-center justify-center relative">
                <div className="w-32 h-32 rounded-full bg-spend-card border border-spend-accent-success/20 flex items-center justify-center">
                  <Lock className="w-16 h-16 text-spend-accent-success" />
                </div>
                
                {/* Orbiting icons */}
                <motion.div 
                  className="absolute -top-4 -right-4 w-12 h-12 rounded-full bg-spend-card border border-spend-border flex items-center justify-center"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "calc(100% + 60px) center" }}
                >
                  <EyeOff className="w-5 h-5 text-spend-accent-primary" />
                </motion.div>
                
                <motion.div 
                  className="absolute -bottom-4 -left-4 w-12 h-12 rounded-full bg-spend-card border border-spend-border flex items-center justify-center"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "calc(100% + 80px) center" }}
                >
                  <Database className="w-5 h-5 text-spend-accent-secondary" />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Dashboard Preview Section
const DashboardPreview = () => {
  const [activeScreen, setActiveScreen] = useState(0);
  const screens = [
    { img: screenshots.mainScreen, label: "Dashboard", desc: "AI insights, budgets & spending overview" },
    { img: screenshots.askAI, label: "Ask AI", desc: "Natural language finance assistant" },
    { img: screenshots.goals, label: "Goals", desc: "Track savings with smart recommendations" },
    { img: screenshots.expenses, label: "Expenses", desc: "Detailed expense breakdown by category" },
    { img: screenshots.budgets, label: "Budgets", desc: "6-month histogram with alerts" },
  ];
  
  return (
    <section className="py-24 px-6 relative overflow-hidden" id="preview">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-spend-bg via-spend-paper/20 to-spend-bg" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-spend-accent-primary/10 border border-spend-accent-primary/20 mb-6">
            <Smartphone className="w-4 h-4 text-spend-accent-primary" />
            <span className="text-sm font-medium text-spend-accent-primary">App Preview</span>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-spend-text-primary" data-testid="preview-title">
            See It In Action
          </h2>
          <p className="text-spend-text-secondary max-w-2xl mx-auto">
            A clean, intuitive interface that puts your finances at your fingertips
          </p>
        </motion.div>
        
        {/* 3D Interactive Phone Display */}
        <div className="relative perspective-container mb-16 pt-8">
          {/* Background 3D elements */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[600px] h-[600px] rounded-full bg-spend-accent-primary/5 blur-3xl animate-pulse" />
          </div>
          
          {/* Floating decorative elements - repositioned */}
          <motion.div 
            className="absolute top-32 left-[10%] w-16 h-16 rounded-2xl bg-spend-accent-primary/10 border border-spend-accent-primary/20 backdrop-blur-sm hidden lg:flex items-center justify-center z-0"
            animate={{ 
              y: [0, -15, 0],
              rotateZ: [0, 5, 0],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Brain className="w-7 h-7 text-spend-accent-primary" />
          </motion.div>
          
          <motion.div 
            className="absolute bottom-40 right-[10%] w-14 h-14 rounded-xl bg-spend-accent-success/10 border border-spend-accent-success/20 backdrop-blur-sm hidden lg:flex items-center justify-center z-0"
            animate={{ 
              y: [0, 15, 0],
              rotateZ: [0, -5, 0],
            }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          >
            <Target className="w-5 h-5 text-spend-accent-success" />
          </motion.div>
          
          <motion.div 
            className="absolute top-1/2 right-[5%] w-12 h-12 rounded-lg bg-spend-accent-warning/10 border border-spend-accent-warning/20 backdrop-blur-sm hidden xl:flex items-center justify-center z-0"
            animate={{ 
              y: [0, -10, 0],
              x: [0, 5, 0],
            }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          >
            <Wallet className="w-5 h-5 text-spend-accent-warning" />
          </motion.div>

          {/* Main 3D Phone Carousel */}
          <motion.div 
            className="relative flex justify-center items-center py-16"
            style={{ minHeight: '580px' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* All 5 phones in 3D arrangement */}
            {screens.map((screen, index) => {
              const isActive = activeScreen === index;
              const offset = index - activeScreen;
              const absOffset = Math.abs(offset);
              
              // Calculate 3D transform values
              const translateX = offset * 180;
              const translateZ = isActive ? 0 : -150 - absOffset * 50;
              const rotateY = offset * -25;
              const scale = isActive ? 1 : 0.75 - absOffset * 0.1;
              const opacity = isActive ? 1 : Math.max(0.3, 0.6 - absOffset * 0.15);
              const zIndex = 10 - absOffset;
              
              // Only show phones within range
              if (absOffset > 2) return null;
              
              return (
                <motion.div
                  key={screen.label}
                  className={`absolute cursor-pointer ${isActive ? 'z-20' : ''}`}
                  onClick={() => setActiveScreen(index)}
                  initial={false}
                  animate={{
                    x: translateX,
                    z: translateZ,
                    rotateY: rotateY,
                    scale: scale,
                    opacity: opacity,
                  }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 300, 
                    damping: 30,
                    mass: 1
                  }}
                  style={{ 
                    zIndex: zIndex,
                    transformStyle: 'preserve-3d',
                    perspective: '1000px'
                  }}
                  whileHover={!isActive ? { scale: scale + 0.05, opacity: opacity + 0.2 } : {}}
                  data-testid={isActive ? "main-phone-mockup" : `phone-${index}`}
                >
                  <div className={`relative ${isActive ? 'phone-3d-active' : 'phone-3d-side'}`}>
                    {/* Phone frame */}
                    <div className={`relative rounded-[40px] overflow-hidden bg-spend-bg transition-shadow duration-500 ${
                      isActive 
                        ? 'w-[280px] sm:w-[320px] border-[3px] border-spend-accent-primary/60 shadow-[0_0_60px_rgba(139,92,246,0.4)]' 
                        : 'w-[240px] sm:w-[280px] border-2 border-spend-border/40 shadow-2xl'
                    }`}>
                      {/* Notch */}
                      <div className={`absolute top-3 left-1/2 -translate-x-1/2 h-6 bg-spend-bg rounded-full z-20 border border-spend-border/30 ${
                        isActive ? 'w-24' : 'w-20'
                      }`} />
                      
                      {/* Screen */}
                      <img 
                        src={screen.img}
                        alt={screen.label}
                        className="w-full h-auto"
                        width={320}
                        height={693}
                        loading="lazy"
                        decoding="async"
                      />
                      
                      {/* Glass overlay */}
                      <div className={`absolute inset-0 pointer-events-none ${
                        isActive 
                          ? 'bg-gradient-to-br from-white/10 via-transparent to-transparent' 
                          : 'bg-gradient-to-br from-white/5 via-transparent to-black/20'
                      }`} />
                      
                      {/* Active glow ring */}
                      {isActive && (
                        <motion.div 
                          className="absolute -inset-1 rounded-[44px] bg-gradient-to-r from-spend-accent-primary via-spend-accent-secondary to-spend-accent-primary opacity-30 blur-md -z-10"
                          animate={{ 
                            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                          }}
                          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                          style={{ backgroundSize: '200% 200%' }}
                        />
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
          
          {/* Active screen label - positioned outside carousel */}
          <motion.div 
            className="text-center mt-8"
            key={`label-${activeScreen}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="font-heading font-bold text-xl text-spend-text-primary">{screens[activeScreen].label}</h3>
            <p className="text-spend-text-secondary text-sm mt-1">{screens[activeScreen].desc}</p>
          </motion.div>
        </div>
        
        {/* Interactive screen selector */}
        <div className="flex justify-center gap-3 mb-16 flex-wrap">
          {screens.map((screen, index) => (
            <motion.button
              key={screen.label}
              onClick={() => setActiveScreen(index)}
              className={`group relative px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                activeScreen === index 
                  ? 'bg-spend-accent-primary text-white shadow-lg shadow-spend-accent-primary/40' 
                  : 'bg-spend-paper/80 backdrop-blur-sm border border-spend-border text-spend-text-secondary hover:text-spend-text-primary hover:border-spend-accent-primary/50 hover:bg-spend-card'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid={`screen-selector-${index}`}
            >
              <span className="relative z-10">{screen.label}</span>
              {activeScreen === index && (
                <motion.div
                  className="absolute inset-0 rounded-full bg-gradient-to-r from-spend-accent-primary to-spend-accent-secondary"
                  layoutId="activeTabBg"
                  style={{ zIndex: 0 }}
                  transition={{ type: "spring", bounce: 0.25, duration: 0.6 }}
                />
              )}
            </motion.button>
          ))}
        </div>
        
        {/* Feature highlights with 3D cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
          <motion.div 
            className="group relative p-6 rounded-2xl bg-spend-paper/50 border border-spend-border backdrop-blur-sm hover:bg-spend-card hover:border-spend-accent-primary/30 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            whileHover={{ y: -5, rotateX: 5 }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-spend-accent-primary/20 to-spend-accent-secondary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <MessageSquare className="w-7 h-7 text-spend-accent-primary" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-spend-text-primary mb-2">Ask SpendArc AI</h3>
            <p className="text-sm text-spend-text-secondary">Natural language queries about your finances</p>
          </motion.div>
          
          <motion.div 
            className="group relative p-6 rounded-2xl bg-spend-paper/50 border border-spend-border backdrop-blur-sm hover:bg-spend-card hover:border-spend-accent-primary/30 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            whileHover={{ y: -5, rotateX: 5 }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-spend-accent-success/20 to-spend-accent-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <Target className="w-7 h-7 text-spend-accent-success" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-spend-text-primary mb-2">Goal Planner</h3>
            <p className="text-sm text-spend-text-secondary">Set savings goals with smart recommendations</p>
          </motion.div>
          
          <motion.div 
            className="group relative p-6 rounded-2xl bg-spend-paper/50 border border-spend-border backdrop-blur-sm hover:bg-spend-card hover:border-spend-accent-primary/30 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            whileHover={{ y: -5, rotateX: 5 }}
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-spend-accent-warning/20 to-spend-accent-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
              <Calculator className="w-7 h-7 text-spend-accent-warning" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-spend-text-primary mb-2">Safe-to-Spend</h3>
            <p className="text-sm text-spend-text-secondary">Check if purchases fit your budget & goals</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Timeline Section
const TimelineSection = () => {
  return (
    <section className="py-24 px-6" id="timeline">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 text-spend-text-primary" data-testid="timeline-title">
            Platform Roadmap
          </h2>
          <p className="text-spend-text-secondary">
            Android v4.0 released • iOS & Desktop coming soon
          </p>
        </motion.div>
        
        {/* Timeline */}
        <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-0">
          {timeline.map((item, index) => (
            <motion.div 
              key={item.phase}
              className={`timeline-item flex-1 flex flex-col items-center ${index !== timeline.length - 1 ? 'md:mr-8' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              data-testid={`timeline-item-${index}`}
            >
              {/* Circle */}
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                item.status === 'current' 
                  ? 'bg-spend-accent-primary text-white shadow-lg shadow-spend-accent-primary/30' 
                  : 'bg-spend-paper border-2 border-spend-border text-spend-text-muted'
              }`}>
                {item.status === 'current' ? (
                  <Zap className="w-7 h-7" />
                ) : (
                  <ChevronRight className="w-6 h-6" />
                )}
              </div>
              
              {/* Label */}
              <h3 className={`font-heading font-bold text-lg mb-1 ${
                item.status === 'current' ? 'text-spend-accent-primary' : 'text-spend-text-primary'
              }`}>
                {item.phase}
              </h3>
              <p className="text-sm text-spend-text-secondary text-center">{item.desc}</p>
              
              {/* Status badge */}
              {item.status === 'current' && (
                <span className="mt-3 px-3 py-1 rounded-full bg-spend-accent-primary/10 text-spend-accent-primary text-xs font-medium">
                  In Progress
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Email Signup Section
const SignupSection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    setStatus({ type: '', message: '' });
    
    try {
      const { default: axios } = await axiosModule();
      const response = await axios.post(`${API}/subscribe`, { email });
      setStatus({ type: 'success', message: response.data.message });
      setEmail('');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Something went wrong. Please try again.';
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <section className="py-24 px-6 relative" id="signup">
      <div className="absolute inset-0 gradient-glow opacity-50" />
      
      <motion.div 
        className="max-w-2xl mx-auto relative z-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="glass rounded-2xl p-8 sm:p-12 border border-spend-border text-center" data-testid="signup-card">
          <div className="w-16 h-16 rounded-full bg-spend-accent-primary/10 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-spend-accent-primary" />
          </div>
          
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4 text-spend-text-primary">
            Get Early Access
          </h2>
          <p className="text-spend-text-secondary mb-8">
            Be the first to know when SpendArc launches. No spam, just updates.
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 rounded-full bg-spend-bg border border-spend-border text-spend-text-primary placeholder:text-spend-text-muted input-focus"
              required
              data-testid="email-input"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="btn-shine px-8 py-3 rounded-full bg-spend-accent-primary text-white font-semibold hover:bg-spend-accent-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-btn"
            >
              {isLoading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Notify Me
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
          
          {/* Status message */}
          {status.message && (
            <motion.div 
              className={`mt-6 flex items-center justify-center gap-2 text-sm ${
                status.type === 'success' ? 'text-spend-accent-success' : 'text-spend-accent-error'
              }`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="status-message"
            >
              {status.type === 'success' ? <Check className="w-4 h-4" /> : null}
              {status.message}
            </motion.div>
          )}
        </div>
      </motion.div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 px-6 border-t border-spend-border">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {/* Logo and copyright */}
          <div className="text-center md:text-left">
            <h3 className="font-heading text-xl font-bold gradient-text mb-2">SpendArc</h3>
            <p className="text-sm text-spend-text-muted">
              © 2026 SpendArc. All rights reserved.
            </p>
            <p className="text-xs text-spend-text-muted mt-1">
              <a
                href="https://github.com/atomickashyap/SpendArc-Public"
                target="_blank"
                rel="noopener noreferrer"
                className="text-spend-accent-primary hover:text-spend-accent-secondary transition-colors inline-flex items-center gap-1"
              >
                <Github className="w-3 h-3" />
                View on GitHub
              </a>
              {" • "}
              Built by{" "}
              <a
                href="https://github.com/atomickashyap"
                target="_blank"
                rel="noopener noreferrer"
                className="text-spend-accent-primary hover:text-spend-accent-secondary transition-colors"
              >
                @atomickashyap
              </a>
            </p>
          </div>
          
          {/* Privacy commitment */}
          <div className="flex items-center gap-3 text-sm text-spend-text-secondary">
            <Shield className="w-4 h-4 text-spend-accent-success" />
            <span>Privacy-first, always.</span>
            <a
              href="/privacy-policy.html"
              className="text-spend-text-secondary hover:text-spend-text-primary underline underline-offset-4"
              data-testid="footer-privacy-policy"
            >
              Privacy Policy
            </a>
          </div>
          
          {/* Social links */}
          <div className="flex items-center gap-4">
            <a 
              href="https://github.com/atomickashyap/SpendArc-Public"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 rounded-full bg-spend-paper border border-spend-border flex items-center justify-center text-spend-text-muted hover:text-spend-text-primary hover:border-spend-border-active transition-colors"
              data-testid="social-github"
              title="View Project on GitHub"
            >
              <Github className="w-5 h-5" />
            </a>
            <a 
              href="mailto:support@spendarc.com" 
              className="w-10 h-10 rounded-full bg-spend-paper border border-spend-border flex items-center justify-center text-spend-text-muted hover:text-spend-text-primary hover:border-spend-border-active transition-colors"
              data-testid="social-mail"
              title="Contact Support"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-spend-bg noise-overlay">
      <HeroSection />
      <div className="section-divider" />
      <FeaturesSection />
      <div className="section-divider" />
      <PrivacySection />
      <div className="section-divider" />
      <DashboardPreview />
      <div className="section-divider" />
      <TimelineSection />
      <div className="section-divider" />
      <SignupSection />
      <Footer />
      <Suspense fallback={null}>
        <SpeedInsights />
      </Suspense>
    </div>
  );
}

export default App;
